import type { VinculoFoto } from '@adelante/shared';
import { type DBSchema, type IDBPDatabase, openDB } from 'idb';
import { ErrorHttp } from './cliente';
import { type FotoComprimida, type Ubicacion, subirComprimida } from './fotos';

/**
 * Cola de fotos de obra en IndexedDB.
 *
 * La cámara entrega fotos ya comprimidas (~300 KB); se guardan aquí y un
 * procesador las sube en orden cuando hay conexión. Así se puede capturar en
 * obra sin señal y las fotos no pasan por la galería del teléfono.
 *
 *   encolarFotos() → guarda y dispara el procesamiento
 *   procesarCola()  → sube las pendientes (una a la vez); idempotente
 *   suscribir()     → notifica cambios (para el contador del header)
 *
 * Errores 4xx (obra inexistente, sin permiso) no se reintentan para siempre:
 * tras 5 intentos la foto queda marcada con error para que el usuario la vea.
 */

const DB_NAME = 'obrascontrol-fotos';
const STORE = 'pendientes';
const MAX_INTENTOS = 5;

export interface FotoPendiente {
  id?: number;
  foto: FotoComprimida;
  vinculo: VinculoFoto;
  /** Texto para mostrar (ej. "VC-F.04 · Colado Losa"). */
  etiqueta: string;
  tomada_en: string;
  ubicacion: Ubicacion | null;
  intentos: number;
  ultimo_error: string | null;
}

interface Schema extends DBSchema {
  pendientes: { key: number; value: FotoPendiente };
}

let dbPromesa: Promise<IDBPDatabase<Schema>> | null = null;
function db() {
  if (!dbPromesa) {
    dbPromesa = openDB<Schema>(DB_NAME, 1, {
      upgrade(d) {
        if (!d.objectStoreNames.contains(STORE))
          d.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      },
    });
  }
  return dbPromesa;
}

// ---------------------------------------------------------------------------
// Suscripción (contador / estado)
// ---------------------------------------------------------------------------

export interface EstadoCola {
  pendientes: number;
  conError: number;
  subiendo: boolean;
}

let estado: EstadoCola = { pendientes: 0, conError: 0, subiendo: false };
const oyentes = new Set<(e: EstadoCola) => void>();

export function suscribir(fn: (e: EstadoCola) => void): () => void {
  oyentes.add(fn);
  fn(estado);
  return () => oyentes.delete(fn);
}

async function publicar(parcial: Partial<EstadoCola> = {}) {
  const todas = await (await db()).getAll(STORE);
  estado = {
    ...estado,
    ...parcial,
    pendientes: todas.length,
    conError: todas.filter((f) => f.intentos >= MAX_INTENTOS).length,
  };
  for (const fn of oyentes) fn(estado);
}

/** Avisa a la galería que hay fotos nuevas en el servidor. */
export const EVENTO_FOTOS_SUBIDAS = 'obc:fotos-subidas';

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export async function encolarFotos(
  fotos: Array<{ foto: FotoComprimida; tomada_en: string; ubicacion: Ubicacion | null }>,
  vinculo: VinculoFoto,
  etiqueta: string,
): Promise<void> {
  const d = await db();
  const tx = d.transaction(STORE, 'readwrite');
  for (const f of fotos) {
    await tx.store.add({ ...f, vinculo, etiqueta, intentos: 0, ultimo_error: null });
  }
  await tx.done;
  await publicar();
  void procesarCola();
}

export async function listarPendientes(): Promise<FotoPendiente[]> {
  return (await db()).getAll(STORE);
}

/** Reintenta las que quedaron con error (reinicia el contador). */
export async function reintentarErrores(): Promise<void> {
  const d = await db();
  for (const f of await d.getAll(STORE)) {
    if (f.intentos >= MAX_INTENTOS) await d.put(STORE, { ...f, intentos: 0, ultimo_error: null });
  }
  await publicar();
  void procesarCola();
}

/** Descarta una foto pendiente (no se sube). */
export async function descartarPendiente(id: number): Promise<void> {
  await (await db()).delete(STORE, id);
  await publicar();
}

let procesando = false;

/** Sube las fotos pendientes, una por una. Seguro de llamar varias veces. */
export async function procesarCola(): Promise<void> {
  if (procesando || !navigator.onLine) return;
  procesando = true;
  await publicar({ subiendo: true });
  let subidas = 0;
  try {
    const d = await db();
    for (const f of await d.getAll(STORE)) {
      if (f.id === undefined || f.intentos >= MAX_INTENTOS) continue;
      if (!navigator.onLine) break;
      try {
        await subirComprimida(f.foto, f.vinculo, {
          tomada_en: f.tomada_en,
          ubicacion: f.ubicacion,
        });
        await d.delete(STORE, f.id);
        subidas++;
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Error desconocido';
        // Error de red (sin respuesta HTTP): se corta y se reintenta luego sin
        // gastar intentos. Error HTTP: cuenta como intento.
        const esHttp = e instanceof ErrorHttp || /HTTP \d{3}/.test(msg);
        if (!esHttp) break;
        // 404/409: la obra no existe o ya no admite fotos (entregada / deshabilitada):
        // no tiene sentido reintentar; queda con error a la vista del usuario.
        const definitivo = e instanceof ErrorHttp && (e.status === 404 || e.status === 409);
        await d.put(STORE, {
          ...f,
          intentos: definitivo ? MAX_INTENTOS : f.intentos + 1,
          ultimo_error: msg,
        });
      }
      await publicar({ subiendo: true });
    }
  } finally {
    procesando = false;
    await publicar({ subiendo: false });
    if (subidas > 0) window.dispatchEvent(new CustomEvent(EVENTO_FOTOS_SUBIDAS));
  }
}

let iniciado = false;
/** Arranca el procesador: al cargar, al recuperar conexión y cada 60 s. */
export function iniciarProcesadorFotos(): void {
  if (iniciado) return;
  iniciado = true;
  window.addEventListener('online', () => void procesarCola());
  setInterval(() => void procesarCola(), 60_000);
  void publicar().then(() => procesarCola());
}
